export type ThemePreference = "light" | "dark";

export const LAST_AUTHENTICATED_THEME_KEY = "waivera:lastAuthenticatedTheme";

export const readStoredTheme = (key: string): ThemePreference | null => {
  try {
    const value = window.localStorage.getItem(key);
    if (value === "dark" || value === "true") return "dark";
    if (value === "light" || value === "false") return "light";
  } catch {
    // Storage may be unavailable in privacy-focused browser sessions.
  }

  return null;
};

export const findLastAuthenticatedTheme = (): ThemePreference | null => {
  const sharedPreference = readStoredTheme(LAST_AUTHENTICATED_THEME_KEY);
  if (sharedPreference) return sharedPreference;

  try {
    // Backward compatibility for preferences saved before the shared key was
    // introduced. The shared key is written the next time the user toggles.
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        key?.startsWith("darkMode:") ||
        key?.startsWith("privilegedDarkMode:") ||
        key?.startsWith("superAdminDarkMode:")
      ) {
        const preference = readStoredTheme(key);
        if (preference) return preference;
      }
    }
  } catch {
    // Fall through to the operating-system preference.
  }

  return null;
};

export const getSystemTheme = (): ThemePreference =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export const applyDocumentTheme = (theme: ThemePreference): void => {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
  root.setAttribute("data-loader-theme", theme);
};

export const resolveAuthenticationTheme = (): ThemePreference =>
  findLastAuthenticatedTheme() ?? getSystemTheme();
